// ================================================
// useHorarios.js — Hook profesional para EKINE
// Maneja horarios, listeners, validaciones y slot
// ================================================

import { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../../firebase/config";
import { REGLAS_SERVICIOS, getReglaServicio } from "../config/reglasServicios";

// =====================================================
// Helpers
// =====================================================

const fmtYMD = (d) => d.toISOString().slice(0, 10);

const parseYMD = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};

//const isWeekday = (d) => {
  //const gd = d.getDay();
  //return gd >= 1 && gd <= 5;
//};

const HHmmToDate = (ymd, hhmm) => {
  const d = parseYMD(ymd);
  const [h, m] = hhmm.split(":").map(Number);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
};

const addMinutes = (date, minutes) =>
  new Date(date.getTime() + minutes * 60000);

const toHHmm = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;

const normalizeHHmm = (t) => {
  if (!t || typeof t !== "string" || !t.includes(":")) return "";
  const [h, m] = t.split(":");
  return `${String(parseInt(h)).padStart(2, "0")}:${String(
    parseInt(m)
  ).padStart(2, "0")}`;
};

const weekdayISO = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const n = d.getDay();
  return n === 0 ? 7 : n;
};

const nextWednesdayFrom = (ymdStr) => {
  let d = parseYMD(ymdStr);
  let loops = 0;
  while (d.getDay() !== 3 && loops < 7) {
    d = addMinutes(d, 24 * 60);
    loops++;
  }
  return fmtYMD(d);
};

const firstAvailableBusinessDay = () => {
  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );
};

const buildSlots30 = (ymd, start = "07:30", end = "20:00") => {
  if (!ymd) return [];
  const out = [];
  let cur = HHmmToDate(ymd, start);
  const endD = HHmmToDate(ymd, end);

  while (cur <= endD) {
    out.push(toHHmm(cur));
    cur = addMinutes(cur, 30);
  }

  return out;
};

// =====================================================
// HOOK PRINCIPAL
// =====================================================

export default function useHorarios({
  fechaSeleccionada,
  categoria,
  variante,
  isMasoterapia,
  isMasajesMitadSemana,
  modalidadKine,
  planEntrada,
  esPrimeraSesionReal,
  publicAvailability = true,
}) {
let esPrimera = esPrimeraSesionReal;

if (typeof esPrimera !== "boolean") {
  esPrimera = false;
  if (planEntrada) {
    esPrimera =
      (planEntrada.sesiones_realizadas ?? 0) === 0 &&
      (planEntrada.sesiones_reservadas ?? 0) === 0;
  }
}


const catLower =
(categoria || "")
.toLowerCase();

const varLower =
(variante || "")
.toLowerCase();

const modalidadParaReglas =
catLower.includes("kine")
? (esPrimera
    ? "privada"
    : modalidadKine)

: catLower.includes("entren")

? (
   modalidadKine ||
   "grupal"
 )

: null;


    
const reglaServicio =
  getReglaServicio(categoria, variante, modalidadParaReglas, esPrimera) ||
  REGLAS_SERVICIOS.default;




  const DURATION_MIN = reglaServicio.duracionMin;
  const BUFFER_MIN = reglaServicio.bufferMin;
  const BLOQUEA_PREVIO = reglaServicio.bloqueaPrevio || 0;
  const BLOQUEA_POSTERIOR = reglaServicio.bloqueaPosterior || 0;
  const SOLO_PRIMERA = reglaServicio.soloPrimeraSesion;

  // =====================================================
  // Estados principales
  // =====================================================

  const [horarios, setHorarios] = useState([]);
  const [estadoPorHora, setEstadoPorHora] = useState(new Map());
  const [ocupacionBloques, setOcupacionBloques] = useState(new Map());
  const [contadorPorSlot, setContadorPorSlot] = useState(new Map());
  const [breaksDelDia, setBreaksDelDia] = useState([]);
  const [weeklyTemplate, setWeeklyTemplate] = useState({});
  const [effectiveBreaks, setEffectiveBreaks] = useState([]);
  const [fechaError, setFechaError] = useState("");
    
  const [weeklySchedule, setWeeklySchedule] = useState({}); 

  const [primarySlot, setPrimarySlot] = useState(null);

  const unsubReservasRef = useRef(null);
  const unsubBreaksRef = useRef(null);

  // =====================================================
  // FECHAS MÍNIMAS
  // =====================================================

  const minDate = fmtYMD(firstAvailableBusinessDay());
  const minDateForService = isMasajesMitadSemana
    ? nextWednesdayFrom(minDate)
    : minDate;

  // =====================================================
  // LISTENER: Horario semanal y plantilla semanal
  // =====================================================

  useEffect(() => {
    const ref = doc(db, "breaks_weekly", "default");

    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};

      setWeeklyTemplate(data.defaultWeekly || {});

      // NUEVO SISTEMA
      if (data.weeklySchedule) {
        setWeeklySchedule(data.weeklySchedule);
      }

      // FALLBACK COMPATIBILIDAD
      else if (data.horarioBase) {
        const start = data.horarioBase.start || "07:30";
        const end = data.horarioBase.end || "20:00";

        const fallback = {};

        [1, 2, 3, 4, 5, 6, 7].forEach((d) => {
          fallback[String(d)] = {
            enabled: d <= 5,
            start,
            end,
          };
        });

        setWeeklySchedule(fallback);
      }
    });

    return () => unsub();
  }, []);

  // =====================================================
  // Cambio de fecha → generar horarios y cargar reservas
  // =====================================================

  useEffect(() => {
    const activeDate = fechaSeleccionada;

    if (!activeDate) {
      setHorarios([]);
      return;
    }

    const d = parseYMD(activeDate);
    const hoy = fmtYMD(new Date());

    // Validaciones
    if (activeDate <= hoy) {
      setFechaError("No puedes reservar hoy ni días anteriores.");
      setHorarios([]);
      return;
    }

    const weekKey = String(weekdayISO(activeDate));
    const dayConfig = weeklySchedule?.[weekKey];

    if (!dayConfig?.enabled) {
      setFechaError("Este día no está disponible para reservas.");
      setHorarios([]);
      return;
    }

    if (isMasoterapia) {
      const ok = [1, 2, 4, 5].includes(d.getDay());
      if (!ok) {
        setFechaError("Masoterapia sólo L, M, J y V.");
        setHorarios([]);
        return;
      }
    }

    if (isMasajesMitadSemana && d.getDay() !== 3) {
      setFechaError("Este servicio solo se agenda los miércoles.");
      setHorarios([]);
      return;
    }

    // cutoff 14:00
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    if (fmtYMD(tomorrow) === activeDate) {
      const cutoff = new Date();
      cutoff.setHours(14, 0, 0, 0);
      if (now >= cutoff) {
        setFechaError("El plazo para agendar mañana venció a las 14:00.");
        setHorarios([]);
        return;
      }
    }

    setFechaError("");

    const start = dayConfig?.start || "07:30";
    const end = dayConfig?.end || "20:00";

    const grid = buildSlots30(activeDate, start, end);

    setHorarios(grid);

    // ======================
    // LISTENERS FIRESTORE
    // ======================

    if (unsubReservasRef.current) unsubReservasRef.current();
    if (unsubBreaksRef.current) unsubBreaksRef.current();

    const qMain = publicAvailability
      ? query(
          collection(db, "agenda_publica"),
          where("fecha", "==", activeDate)
        )
      : query(
          collection(db, "reservas"),
          where("fecha", "==", activeDate),
          where("estado", "in", ["pendiente", "aprobada", "realizada"])
        );

    const qBr = query(collection(db, "breaks"), where("fecha", "==", activeDate));

    const estados = new Map();
    const mapa = new Map();
    const contador = new Map();



const unsub1 = onSnapshot(qMain, (snap) => {
  mapa.clear();
  contador.clear();

  snap.forEach((docSnap) => {
    const r = docSnap.data();
    const horaInicio = normalizeHHmm(r.hora);
    if (!horaInicio) return;

    const catReserva = r.servicio_categoria || r.categoria || r.servicio || "";
    const varReserva = r.servicio_variante || r.variante || r.nombre_variante || "";

    const rawReserva = r.servicio_raw || r.raw || "";

    // ----------------------------
    // Normalizadores / detectores
    // ----------------------------
    const norm = (t) =>
      (t || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[—–-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const catN = norm(catReserva);
    const varN = norm(varReserva);
    const rawN = norm(rawReserva);
    
    // usar modalidad REAL guardada en firestore

    const modalidadNorm =
    (r.modalidad || "")
    .toLowerCase()
    .trim();

    if (!modalidadNorm) {

    console.warn(
    "Reserva sin modalidad explícita:",
    r
    );

    return;

    }

      const esPrimeraFlag =
  r.esPrimeraSesion === true;

    const regla = r.reglaSnapshot ||
    getReglaServicio(
    catReserva,
    varReserva,
    modalidadNorm,
    esPrimeraFlag
    );
    
    const dur = regla.duracionMin ?? 60;
    const base = HHmmToDate(activeDate, horaInicio);

    const esGrupal =
    modalidadNorm === "grupal";

    const esPrivada =
    modalidadNorm === "privada";

    const capReserva = regla?.capacidadMax ?? 2;

    // PRE: solo privadas (bloqueo duro)
    const prev = esPrivada ? (regla.bloqueaPrevio || 0) : 0;

    // POST (defensa):
    // - privadas: su bloqueaPosterior
    // - grupales: SIEMPRE 30 min (para que una privada no use ese tramo como prebuffer)
    const post = esPrivada ? (regla.bloqueaPosterior || 0) : 0;

    const iniPrev = addMinutes(base, -prev);
    const finReal = addMinutes(base, dur);
    const finPost = addMinutes(finReal, post);

    let cursor = new Date(iniPrev);

    while (cursor < finPost) {
      const hhmm = toHHmm(cursor);

      if (!mapa.has(hhmm)) {
      mapa.set(hhmm, {
        total: 0,
        topables: 0,
        exclusiva: false,
        esBloqueReal: false,

        bufferPrevio: false,      // prebuffer de privada
        postBuffer: false,        // defensa visual
        postDePrivada: false,     // 👈 nuevo
        postDeGrupal: false,      // 👈 nuevo

        inicioPrivada: false,

        capacidadMax: 2,
        grupales: 0,
        privadas: 0,
      });
      }

      const info = mapa.get(hhmm);

      const inPrev = prev > 0 && cursor >= iniPrev && cursor < base;
      const inReal = cursor >= base && cursor < finReal;
      const inPost = post > 0 && cursor >= finReal && cursor < finPost;

      if (inPrev) {
        info.bufferPrevio = true;
      } else if (inReal) {
        info.esBloqueReal = true;

        if (esPrivada) {
          info.exclusiva = true;
          info.privadas += 1;
          info.total += 1;

          if (hhmm === horaInicio) info.inicioPrivada = true;

        } else {
          info.topables += 1;
          info.grupales += 1;
          info.capacidadMax = Math.max(info.capacidadMax ?? 2, capReserva);
          info.total += 1;
        }
      } else if (inPost) {
        info.postBuffer = true;
        if (esPrivada) info.postDePrivada = true;
        else info.postDeGrupal = true;
      }

      mapa.set(hhmm, info);
      cursor = addMinutes(cursor, 30);
    }
  });

  mapa.forEach((info, hora) => {
    contador.set(`${activeDate} ${hora}`, info.total);
  });

  setOcupacionBloques(new Map(mapa));
  setContadorPorSlot(new Map(contador));
  setEstadoPorHora(new Map(estados));
});


    // Listener breaks
    const unsub2 = onSnapshot(qBr, (snap) => {
      const arr = [];
      snap.forEach((d) => arr.push(d.data()));
      setBreaksDelDia(arr);
    });

    unsubReservasRef.current = unsub1;
    unsubBreaksRef.current = unsub2;
  }, [
    fechaSeleccionada,
    weeklySchedule,
    isMasajesMitadSemana,
    isMasoterapia,
  ]);

  // =====================================================
  // MERGE breaks semanales + diarios
  // =====================================================

  useEffect(() => {
    if (!fechaSeleccionada) return;

    const wkey = String(weekdayISO(fechaSeleccionada));
    const weekly = weeklyTemplate[wkey] || [];

    setEffectiveBreaks(breaksDelDia.length ? breaksDelDia : weekly);
  }, [fechaSeleccionada, breaksDelDia, weeklyTemplate]);

  // =====================================================
  // Seleccionar un horario
  // =====================================================

const onPickPrimary = (ymd, hhmm) => {
  if (!ymd || !hhmm) return;

  const esPrimeraSesionReal = esPrimera;

    const catLower =
    (categoria || "")
    .toLowerCase();

    const varLower =
    (variante || "")
    .toLowerCase();

    const modalidadParaReglas =
    catLower.includes("kine")
    ? (esPrimera
        ? "privada"
        : modalidadKine)

      : catLower.includes("entren")

      ? (
        modalidadKine ||
        "grupal"
      )

      : null;

  const regla =
    getReglaServicio(categoria, variante, modalidadParaReglas, esPrimeraSesionReal) ||
    REGLAS_SERVICIOS.default;

  const dur = regla.duracionMin ?? 60;

  const inicioReal = HHmmToDate(ymd, hhmm);
  const finReal = addMinutes(inicioReal, dur);

  const esNuevaGrupal = regla?.permiteTopar === true;
  const esNuevaPrivada = !esNuevaGrupal;

const inicioValidacion =
inicioReal;

const finValidacion =
finReal;


  // recorrer en pasos de 30 min desde inicioValidacion hasta finReal
  let cursor = new Date(inicioValidacion);
  

while (cursor < finValidacion) {
    const key = toHHmm(cursor);
    const info = ocupacionBloques.get(key);

        if (hhmm === "14:30") {
      console.log(
      "[14:30]",
      {
        cursor:key,
        info,
        inicioReal: toHHmm(inicioReal),
        finReal: toHHmm(finReal),
        esNuevaPrivada,
        esNuevaGrupal
      }
      );
    }
    if (info) {
          if (esNuevaGrupal) {
            // ✅ Grupal: puede estar en la "zona previa" de una privada
            // (solo bloquea si pisa el bloque REAL exclusivo o si no hay cupo)

            // ✅ Grupal: choca con privada REAL
            if (info.exclusiva && info.esBloqueReal) {
              alert("Este horario choca con una sesión privada existente.");
              return;
            }

            // ✅ Grupal: respeta cupos
            const cap =
              Math.max(
              info.capacidadMax ?? 2,
              regla.capacidadMax ?? 2
              );
            if ((info.topables ?? 0) >= cap) {
              alert("Este horario ya no tiene cupo (capacidad completa).");
              return;
            }
            } else {
              const enZonaPrevio =
              cursor < inicioReal;

              if (enZonaPrevio) {

                const hayGrupal =
                (info.grupales ?? 0) > 0;

                if (
                  info.esBloqueReal &&
                  hayGrupal
                ) {

                  alert(
                  "Este horario choca con una sesión existente."
                  );

                  return;

                }

              }

              else {

                const hayPrivada =
                (info.privadas ?? 0) > 0;

                if (
                info.esBloqueReal &&
                hayPrivada
                ) {

                alert(
                "Este horario choca con una sesión existente."
                );

                return;

                }
              }

            }
        }
    

    cursor = addMinutes(cursor, 30);
  }

  setPrimarySlot({ fecha: ymd, hora: hhmm });
};

  // =====================================================
  // RETURN
  // =====================================================

  return {
    horarios,
    estadoPorHora,
    ocupacionBloques,
    contadorPorSlot,
    effectiveBreaks,
    fechaError,
    minDate,
    minDateForService,
    primarySlot,
    setPrimarySlot,
    onPickPrimary,
    weeklySchedule,
    setWeeklySchedule,
  };
}
